import type { AdminUserProvisionInput } from '@atlas/models'
import type { JobFunction, UserRole } from '@atlas/types'
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UserDTO } from '@/services/user.service'
import { userApi } from '@/services/user.service'

/**
 * React-query hooks for the canonical user directory (ADR-0013). Serves the
 * SUPER_ADMIN user-management surface, the Work Order assignee picker, and
 * author/actor display-name lookups — one `GET /users` fetch, one cache entry.
 * The set changes rarely, so it's cached for a while.
 */

const USERS_KEY = ['users'] as const

export const useUsers = () => useQuery({ queryKey: USERS_KEY, queryFn: userApi.listUsers, staleTime: 5 * 60 * 1000 })

/**
 * Every active PM, for the PM pickers: the directory is paged, so it cannot promise them all.
 * Under the directory's key, so a user change that invalidates the directory refetches it too.
 */
export const useActivePms = () =>
	useQuery({ queryKey: [...USERS_KEY, 'active-pms'], queryFn: userApi.listActivePms, staleTime: 5 * 60 * 1000 })

/** What a rejected write has to put back: the mutated user's prior field values. */
interface UserRollback {
	userId: string
	previous: Partial<UserDTO> | undefined
}

/**
 * Patch one user in the cached directory ahead of the server, returning the
 * prior values of the fields it touched.
 *
 * The row reflects the change immediately, and a rejected write puts the old
 * value back — so the directory never displays a value the backend refused. That
 * matters here: a role or status change can be denied by the
 * last-active-SUPER_ADMIN guard, and a row left showing the attempted value
 * would be a lie about who can still administer the tenant.
 *
 * Only the mutated fields of the mutated user are captured, NOT a snapshot of
 * the whole list: two edits to different users can be in flight at once, and
 * restoring a whole-list snapshot would revert the other user's change along
 * with this one.
 */
async function optimisticUserPatch(qc: QueryClient, userId: string, patch: Partial<UserDTO>): Promise<UserRollback> {
	await qc.cancelQueries({ queryKey: USERS_KEY })
	const users = qc.getQueryData<UserDTO[]>(USERS_KEY)
	const target = users?.find((u) => u.userId === userId)
	const previous = target
		? (Object.fromEntries(Object.keys(patch).map((key) => [key, target[key as keyof UserDTO]])) as Partial<UserDTO>)
		: undefined

	if (users) {
		qc.setQueryData<UserDTO[]>(
			USERS_KEY,
			users.map((u) => (u.userId === userId ? { ...u, ...patch } : u)),
		)
	}
	return { userId, previous }
}

/** Restore just the fields this write tried to change, leaving concurrent edits alone. */
function rollbackUsers(qc: QueryClient, ctx: UserRollback | undefined): void {
	if (!ctx?.previous) return
	const users = qc.getQueryData<UserDTO[]>(USERS_KEY)
	if (!users) return
	qc.setQueryData<UserDTO[]>(
		USERS_KEY,
		users.map((u) => (u.userId === ctx.userId ? { ...u, ...ctx.previous } : u)),
	)
}

// Provisioning adds a row rather than editing one, and the server assigns the id
// — there is nothing to patch optimistically, so it just invalidates.
export const useProvisionUser = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (body: AdminUserProvisionInput) => userApi.provisionUser(body),
		onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
	})
}

export const useChangeUserRole = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ userId, role }: { userId: string; role: UserRole }) => userApi.changeUserRole(userId, role),
		onMutate: ({ userId, role }) => optimisticUserPatch(qc, userId, { role }),
		onError: (_error, _vars, ctx) => rollbackUsers(qc, ctx),
		onSettled: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
	})
}

export const useChangeUserJobFunction = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ userId, jobFunction }: { userId: string; jobFunction: JobFunction }) =>
			userApi.changeUserJobFunction(userId, jobFunction),
		onMutate: ({ userId, jobFunction }) => optimisticUserPatch(qc, userId, { jobFunction }),
		onError: (_error, _vars, ctx) => rollbackUsers(qc, ctx),
		onSettled: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
	})
}

export const useChangeUserStatus = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ userId, isActive }: { userId: string; isActive: boolean }) =>
			userApi.changeUserStatus(userId, isActive),
		onMutate: ({ userId, isActive }) => optimisticUserPatch(qc, userId, { isActive }),
		onError: (_error, _vars, ctx) => rollbackUsers(qc, ctx),
		onSettled: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
	})
}
